import {createFeatureSelector, createSelector} from '@ngrx/store';
import {adapter, featureKey, State} from '../_reducers/template-entity.reducer';
import {selectLoggedUser} from 'app/_selectors/user.selectors';
import {
	sortFilterTemplatesByUsageCountAndActiveStatus,
	sortFilterClinicTemplates,
	sortFilterGlobalTemplates
} from '../_reducers/sortFilterTemplatesByMineOthers.fn';
import {Dictionary} from '@ngrx/entity';
import {ITemplate, ITemplateAndFields} from '../_interface/full-template';
import * as fromTemplateFields from './template-field-entity.selectors';
import {IFullTemplateField} from '../_interface/itemplate-field';
import {ALL_PROFESSIONS, GLOBAL_TEMPLATES_CLINIC_ID} from 'shared/magic';
import * as fromEntryEntity from './entry-entity.selectors';

export const entityState = createFeatureSelector<State>(featureKey);
export const selectTemplatesLoaded = createSelector(entityState, ({loaded}) => loaded);
export const selectFilterActive = createSelector(entityState, state => state.filterTemplates.active);
export const selectFilterInUse = createSelector(entityState, state => state.filterTemplates.inUse);
export const selectAllTemplateEntities = createSelector(entityState, adapter.getSelectors().selectAll);
export const selectClinicTemplatesForManage = createSelector(
	selectAllTemplateEntities, selectLoggedUser,
	(templates, {clinicId, profession}) => {
		const templatesByProfession = templates
			.filter(t => t.professions.includes(ALL_PROFESSIONS) || t.professions.includes(profession));
		return clinicId !== GLOBAL_TEMPLATES_CLINIC_ID ?
			sortFilterClinicTemplates(templatesByProfession) : sortFilterGlobalTemplates(templatesByProfession);
	}
);

export const selectGlobalTemplatesForManage = createSelector(
	selectAllTemplateEntities, selectLoggedUser,
	(templates, {clinicId}) => {
		return clinicId !== GLOBAL_TEMPLATES_CLINIC_ID ? sortFilterGlobalTemplates(templates) : [];
	}
);

export const selectEntities = createSelector(entityState, adapter.getSelectors().selectEntities);

export const selectOneEntity = (id: string | number) =>
	createSelector(selectEntities, (e: Dictionary<ITemplate>) => e[id]);

export const selectTemplateAndFields = (id: string | number) =>
	createSelector(selectEntities, fromTemplateFields.selectEntities, (templates, tFields): ITemplateAndFields => {
		let template = templates[id];
		const fields: Dictionary<IFullTemplateField> = template?.fields.reduce((acc, item) => ({...acc, [item]: tFields[item]}), {});
		const corrupted = fields
			? Object.values(fields).some(field => {
				if (field && 'corrupted' in field) { return field.corrupted; }
				return !template || !fields;
			})
			: true;
		const defaultLayout = !!fields ? {
			rows: Object.values(fields).map((field: IFullTemplateField) => [{
				templateFieldId: field.templateFieldId,
				size: 12
			}])
		} : null;
		template = !!template?.layout ? template : {...template, layout: defaultLayout};
		return {template, fields, corrupted};
	});

export const selectClinicTemplatesByUsageCountAndActiveStatus = createSelector(
	selectFilterActive, selectFilterInUse, selectClinicTemplatesForManage,
	(active, inUse, clinicTemplates) =>
		sortFilterTemplatesByUsageCountAndActiveStatus(active, inUse, clinicTemplates)
);

export const selectGlobalTemplatesByUsageCountAndActiveStatus = createSelector(
	selectFilterActive, selectFilterInUse, selectGlobalTemplatesForManage,
	(active, inUse, globalTemplates) =>
		sortFilterTemplatesByUsageCountAndActiveStatus(active, inUse, globalTemplates)
);

export const selectAllActiveTemplatesAlphabetically = createSelector(
	selectClinicTemplatesForManage,
	selectGlobalTemplatesForManage,
	(mine, others) => {
		const mineActive = mine.filter(t => t.active);
		const othersActive = others.filter(t => t.active);
		const alphabetically = [...mineActive, ...othersActive].sort((a, b) => a.name.localeCompare(b.name));
		return alphabetically.filter((template) => template.active && !template.deprecated);
	}
);

export const selectTemplatesUsedInEntries = createSelector(
	selectEntities, fromEntryEntity.selectEntities,
	(templateEntities, entryEntities) => {
		const usedTemplateIds = new Set(Object.values(entryEntities)
			.filter(e => !!e.templateId)
			.map(e => e.templateId));
		return Array.from(usedTemplateIds).map(id => templateEntities[id]).filter(t => !!t);
	}
);
